import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

interface WelcomeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WelcomeDialog({ open, onOpenChange }: WelcomeDialogProps) {
  const [step, setStep] = useState(1);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem("hideTemplateBuilderWelcome", "true");
    }
    onOpenChange(false);
  };

  const steps = [
    {
      title: "Welcome to Template Builder! 🎨",
      description: "Create beautiful, customizable proposal templates with drag-and-drop simplicity.",
      content: (
        <div className="space-y-4 py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold">1</span>
            </div>
            <div>
              <h4 className="font-medium">Drag blocks from the left</h4>
              <p className="text-sm text-muted-foreground">Choose from text, images, shapes, icons, pricing tables, and more</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold">2</span>
            </div>
            <div>
              <h4 className="font-medium">Design on the canvas</h4>
              <p className="text-sm text-muted-foreground">Position, resize, and arrange blocks freely</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold">3</span>
            </div>
            <div>
              <h4 className="font-medium">Style on the right</h4>
              <p className="text-sm text-muted-foreground">Customize colors, fonts, borders, and effects</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Pro Tips for Building 💡",
      description: "Master these features to build faster",
      content: (
        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Smart Fields</h4>
            <p className="text-sm text-muted-foreground">Type <code className="bg-background px-1 rounded">{"{{customer_name}}"}</code> to insert dynamic data that auto-fills per proposal</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Grid & Snap</h4>
            <p className="text-sm text-muted-foreground">Enable grid and snap-to-grid for perfect alignment</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Keyboard Shortcuts</h4>
            <p className="text-sm text-muted-foreground">
              <kbd className="bg-background px-1.5 py-0.5 rounded text-xs">⌘ Z</kbd> Undo • 
              <kbd className="bg-background px-1.5 py-0.5 rounded text-xs ml-2">⌘ D</kbd> Duplicate • 
              <kbd className="bg-background px-1.5 py-0.5 rounded text-xs ml-2">Del</kbd> Delete
            </p>
          </div>
          <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h4 className="font-medium">AI Assistant</h4>
            </div>
            <p className="text-sm text-muted-foreground">Use AI to generate cover pages, scope of work, terms, and more!</p>
          </div>
        </div>
      ),
    },
  ];

  const currentStep = steps[step - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{currentStep.title}</DialogTitle>
          <DialogDescription>{currentStep.description}</DialogDescription>
        </DialogHeader>

        {currentStep.content}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked as boolean)}
            />
            <Label htmlFor="dontShowAgain" className="text-sm cursor-pointer">
              Don't show again
            </Label>
          </div>

          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            {step < steps.length ? (
              <Button size="sm" onClick={() => setStep(step + 1)}>
                Next
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={handleClose}>
                Get Started
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
